import { Contract, providers, utils } from 'ethers';
import { APERTUM_CHAIN_ID, APERTUM_RPC } from './apertumChain';
import { multicallRead, type MulticallCall } from './multicall3';
import {
  REGISTRY_ABI,
  SPORTS_FUTURE_REGISTRY,
} from './registryConfig';
import type { Eip1193Provider } from './walletConnect';

export const REGISTRY_ADMIN_ROLE = utils.keccak256(
  utils.toUtf8Bytes('ADMIN_ROLE')
);
export const REGISTRY_REVIEWER_ROLE = utils.keccak256(
  utils.toUtf8Bytes('REVIEWER_ROLE')
);

const APPLICATION_READ_CHUNK = 80;

const TESTNET_RPC =
  'https://rpc-testnet.apertum.io/ext/bc/V8YHL6yZV62tx2MdLi98wjAY2Qn6sP7zdiEKX5bvxj515vhK7/rpc';

const READ_TIMEOUT_MS = 15_000;

export function registryReadProvider(chainId: number): providers.JsonRpcProvider {
  if (chainId === 89898) {
    return new providers.StaticJsonRpcProvider(TESTNET_RPC, {
      name: 'apertum-testnet',
      chainId: 89898,
    });
  }
  return new providers.StaticJsonRpcProvider(APERTUM_RPC, {
    name: 'apertum',
    chainId: APERTUM_CHAIN_ID,
  });
}

async function withTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${READ_TIMEOUT_MS / 1000}s`)),
      READ_TIMEOUT_MS
    );
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const APP_ROLE = {
  athlete: 1,
  agent: 2,
  officer: 3,
  representative: 4,
} as const;

export const APP_STATUS = {
  pending: 1,
  approved: 2,
  rejected: 3,
} as const;

export type AppRoleName = keyof typeof APP_ROLE;

export interface OnChainApplication {
  id: number;
  applicant: string;
  didId: number;
  appRole: number;
  status: number;
  contentHash: string;
  uri: string;
  submittedAt: number;
}

export function registryAddress(chainId: number): string {
  if (chainId === 89898) return SPORTS_FUTURE_REGISTRY[89898].registry;
  return SPORTS_FUTURE_REGISTRY[2786].registry;
}

export function registryContract(
  chainId: number,
  runner?: providers.Provider | providers.JsonRpcSigner
): Contract {
  return new Contract(
    registryAddress(chainId),
    REGISTRY_ABI,
    runner ?? registryReadProvider(chainId)
  );
}

export function signerFromEip1193(
  raw: Eip1193Provider
): providers.JsonRpcSigner {
  const web3 = new providers.Web3Provider(raw as providers.ExternalProvider);
  return web3.getSigner();
}

/** hasRole must be the boolean true — a successful call with false/0x00 is not admin. */
export function isGrantedRoleResult(result?: {
  success?: boolean;
  data?: unknown;
} | null): boolean {
  return Boolean(result?.success && result.data === true);
}

export async function readOnChainAdmin(
  chainId: number,
  address: string
): Promise<boolean> {
  if (!address) return false;
  const provider = registryReadProvider(chainId);
  const target = registryAddress(chainId);
  const calls: MulticallCall[] = [
    {
      address: target,
      abi: REGISTRY_ABI,
      functionName: 'hasRole',
      args: [REGISTRY_ADMIN_ROLE, address],
    },
    {
      address: target,
      abi: REGISTRY_ABI,
      functionName: 'hasRole',
      args: [REGISTRY_REVIEWER_ROLE, address],
    },
  ];
  try {
    const results = await withTimeout(
      multicallRead(calls, { provider, chunkSize: 2 }),
      'Registry hasRole multicall'
    );
    return isGrantedRoleResult(results[0]) || isGrantedRoleResult(results[1]);
  } catch {
    const c = registryContract(chainId, provider);
    const [isAdmin, isReviewer] = await Promise.all([
      c.hasRole(REGISTRY_ADMIN_ROLE, address) as Promise<boolean>,
      c.hasRole(REGISTRY_REVIEWER_ROLE, address) as Promise<boolean>,
    ]);
    return Boolean(isAdmin || isReviewer);
  }
}

export async function submitToRegistry(opts: {
  rawProvider: Eip1193Provider;
  chainId: number;
  role: AppRoleName;
  contentHash: string;
  uri: string;
}): Promise<{ txHash: string; applicationId: number }> {
  const signer = signerFromEip1193(opts.rawProvider);
  const c = registryContract(opts.chainId, signer);
  const tx = await c.submit(APP_ROLE[opts.role], opts.contentHash, opts.uri);
  const receipt = await tx.wait();
  if (receipt.status !== 1) throw new Error('Registry submit failed');
  let applicationId = 0;
  try {
    const count = await c.applicationCount();
    applicationId = Number(count) - 1;
  } catch {
    applicationId = 0;
  }
  return { txHash: receipt.transactionHash, applicationId };
}

export async function reviewOnRegistry(opts: {
  rawProvider: Eip1193Provider;
  chainId: number;
  id: number;
  status: 'approved' | 'rejected';
}): Promise<string> {
  const signer = signerFromEip1193(opts.rawProvider);
  const c = registryContract(opts.chainId, signer);
  const tx = await c.review(opts.id, APP_STATUS[opts.status]);
  const receipt = await tx.wait();
  if (receipt.status !== 1) throw new Error('Registry review failed');
  return receipt.transactionHash;
}

export interface RegistryDashboardStats {
  totalAthletes: number;
  totalAgents: number;
  totalOfficers: number;
  pendingApplications: number;
}

export const EMPTY_REGISTRY_STATS: RegistryDashboardStats = {
  totalAthletes: 0,
  totalAgents: 0,
  totalOfficers: 0,
  pendingApplications: 0,
};

export function parseApplicationRow(id: number, row: unknown): OnChainApplication {
  const r = row as Record<string, unknown> & unknown[];
  return {
    id,
    applicant: String(r.applicant ?? r[0] ?? ''),
    didId: Number(r.didId ?? r[1] ?? 0),
    appRole: Number(r.appRole ?? r[2] ?? 0),
    status: Number(r.status ?? r[3] ?? 0),
    contentHash: String(r.contentHash ?? r[4] ?? ''),
    uri: String(r.uri ?? r[5] ?? ''),
    submittedAt: Number(r.submittedAt ?? r[6] ?? 0),
  };
}

export function summarizeRegistryApplications(
  rows: OnChainApplication[]
): RegistryDashboardStats {
  return {
    totalAthletes: rows.filter((r) => r.appRole === APP_ROLE.athlete).length,
    totalAgents: rows.filter((r) => r.appRole === APP_ROLE.agent).length,
    totalOfficers: rows.filter((r) => r.appRole === APP_ROLE.officer).length,
    pendingApplications: rows.filter((r) => r.status === APP_STATUS.pending).length,
  };
}

export async function listRegistryApplications(
  chainId: number
): Promise<OnChainApplication[]> {
  const provider = registryReadProvider(chainId);
  const c = registryContract(chainId, provider);
  const count = Number(
    await withTimeout(c.applicationCount(), 'Registry applicationCount')
  );
  if (!Number.isFinite(count) || count <= 0) return [];

  const target = registryAddress(chainId);
  const calls: MulticallCall[] = Array.from({ length: count }, (_, id) => ({
    address: target,
    abi: REGISTRY_ABI,
    functionName: 'getApplication',
    args: [id],
  }));

  try {
    const results = await withTimeout(
      multicallRead(calls, { provider, chunkSize: APPLICATION_READ_CHUNK }),
      'Registry getApplication multicall'
    );
    const out: OnChainApplication[] = [];
    for (let i = 0; i < results.length; i++) {
      if (!results[i]?.success || results[i].data == null) continue;
      out.push(parseApplicationRow(i, results[i].data));
    }
    return out;
  } catch {
    const out: OnChainApplication[] = [];
    for (let i = 0; i < count; i++) {
      const row = await withTimeout(
        c.getApplication(i),
        `Registry getApplication(${i})`
      );
      out.push(parseApplicationRow(i, row));
    }
    return out;
  }
}

export interface RegistryUser {
  didId: number;
  registeredAt: number;
  active: boolean;
}

export async function getRegistryUser(
  chainId: number,
  address: string
): Promise<RegistryUser> {
  const c = registryContract(chainId);
  const row = (await withTimeout(c.getUser(address), 'Registry getUser')) as {
    didId?: unknown;
    registeredAt?: unknown;
    active?: unknown;
    0?: unknown;
    1?: unknown;
    2?: unknown;
  };
  return {
    didId: Number(row.didId ?? row[0] ?? 0),
    registeredAt: Number(row.registeredAt ?? row[1] ?? 0),
    active: Boolean(row.active ?? row[2] ?? false),
  };
}

export function statusLabel(status: number): 'pending' | 'approved' | 'rejected' {
  if (status === APP_STATUS.approved) return 'approved';
  if (status === APP_STATUS.rejected) return 'rejected';
  return 'pending';
}

export function roleLabel(role: number): AppRoleName {
  if (role === 2) return 'agent';
  if (role === 3) return 'officer';
  if (role === 4) return 'representative';
  return 'athlete';
}

/** @deprecated New submits use ipfs://cid from persistApplication. */
export function sealedUri(contentHash: string): string {
  return `sf://sealed/${contentHash}`;
}

export { ipfsUri } from './ipfs';

export function isHexHash(value: string): boolean {
  return utils.isHexString(value) && value.length === 66;
}
